import { html, css, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';

import '@awesome.me/webawesome/dist/components/button/button.js';

import NSAutoPaymentsCard from './card-base.js';
import JobSchema from '../../../../../models/job-schema.js';
import definition from '@job-schemas/public-ledger/auto-payments/change-target.json';
import { jobSchema } from '../../../job-schema.js';
import { AUTOPAY_CARD_OUTCOMES } from './presentations.js';
import { changeTargetFigures, changeTargetRows, changeTargetText } from '../../../../../helpers/auto-payment-cards.js';
import { CHANGE_STARTING, CHANGE_START_FAILED } from '../../../../../helpers/auto-payment-changes.js';

import '../../../../../ns-elements/ns-job-layout.js';
import '../../../../../ns-elements/ns-detail-list.js';

/**
 * Change the automatic payments target (#25 phase 4, DECISION 1.6): the company's £ target buys less than the signatories'
 * price alert of the credits it bought at signing (BELOW_PERCENT: charges go on), or no more than the company threshold
 * (STOPPED: automatic charges stop). One open card per company, claimed by a signatory. In this phase it shows the
 * problem: liquidity-check keeps its figures up to date and closes it `resolved` once the target buys enough again.
 * Change target (#25 phase 6) starts Change automatic payments amounts as this card's child, for the signatory who
 * pressed it; the card closes when the change ends.
 * A signed stop (#40) closes it `stopped` (public-ledger/autopay/stop-finish).
 */
@customElement(NSPublicLedgerAutoPaymentsChangeTarget.is)
export default class NSPublicLedgerAutoPaymentsChangeTarget extends NSAutoPaymentsCard {
  static get is() {
    return 'ns-public-ledger-auto-payments-change-target';
  }

  static get schema(): JobSchema {
    // Every outcome is hidden: presentations.ts says whose each one is
    return jobSchema(definition, {
      outcomes: AUTOPAY_CARD_OUTCOMES['@ns/public-ledger-auto-payments-change-target'],
      icon: 'bank-card',
    });
  }

  static styles = [
    ...super.styles,
    css`
      .job-panel ns-detail-list {
        --ns-detail-list-gap: var(--wa-space-2xs);
      }
    `,
  ];

  private async __change() {
    await this._startChange('change', CHANGE_STARTING, CHANGE_START_FAILED);
  }

  render() {
    const figures = changeTargetFigures(this._job?.metadata);
    const stopped = figures.reason === 'STOPPED';
    return html`
      <ns-job-layout>
        <p slot="intro">
          The price of nodeStream credits has moved, so your company’s automatic payments target buys fewer credits.
        </p>
        ${this._renderBlocked()}
        ${this._renderWaiting() ??
        (this._claimed || this._completed
          ? html`
              <div class="job-panel">
                <h3>${stopped ? 'Automatic charges have stopped' : 'The target buys fewer credits'}</h3>
                <p>${changeTargetText(figures)}</p>
                ${this._completed
                  ? nothing
                  : html`
                      <p>
                        This job closes by itself once the target buys enough credits
                        again${stopped ? ', and automatic charges start again then' : ''}.
                      </p>
                    `}
              </div>
            `
          : this._renderClaimNote('see what has changed'))}
        <div class="job-panel">
          <h3>The figures</h3>
          <ns-detail-list .rows=${changeTargetRows(figures)}></ns-detail-list>
        </div>
        <div slot="aside" class="primary-info">
          <span class="info-icon" aria-hidden="true">i</span>
          <h3>Why the target buys less</h3>
          <p>
            The company account’s target is in pounds and its threshold in credits. When the price per credit rises, the
            target buys fewer credits, while the threshold stays the same.
          </p>
          <p>
            Charges stop when the target would buy no more credits than the threshold, because a charge could no longer
            lift the account above it.
          </p>
        </div>
      </ns-job-layout>
    `;
  }

  renderActions() {
    if (this._completed || this._waiting !== null) return nothing;
    if (!this._claimed) return this._renderClaimButton();
    return html`
      <wa-button variant="brand" type="button" ?disabled=${this._busy} @click=${() => this.__change()}>
        Change target
      </wa-button>
    `;
  }
}
